<template>
  <safe-teleport to="#toolbar-portal">
    <q-toolbar
      class="q-px-sm row justify-center"
      :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
      style="height: 75px"
      :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
    >
      <div class="market-report full-width row">
        <q-input
          v-if="isEditMode"
          v-model="reportCopy.name"
          filled
          style="font-size: 20px; font-weight: 500"
          placeholder="Enter a name"
          dense
          clearable
        />
        <div v-else style="font-size: 20px; font-weight: 500" class="q-mr-sm q-my-auto">
          New Regional Report
        </div>
        <q-space />
        <div class="q-gutter-x-sm q-my-auto">
          <q-btn
            v-if="isEditMode"
            color="red"
            outline
            :loading="deleteLoading"
            no-caps
            flat
            @click="deleteDialog = true"
          >
            Delete
          </q-btn>
          <q-btn
            v-if="isEditMode"
            outline
            no-caps
            flat
            :to="{ name: 'Regional Report', params: { reportId: report.id } }"
          >
            Cancel
          </q-btn>
          <q-btn v-else no-caps flat unelevated :to="{ name: 'Regional Reports' }"> Cancel </q-btn>
          <q-btn
            no-caps
            unelevated
            color="accent"
            :loading="isSubmitting"
            :disable="!canSubmit"
            @click="submit"
          >
            {{ isEditMode ? 'Save Report' : 'Generate Report' }}
          </q-btn>
        </div>
      </div>
    </q-toolbar>
  </safe-teleport>
  <div v-if="reportCopy" class="market-report q-gutter-y-lg q-mx-auto">
    <enter-markets :report="reportCopy" @update-report="updateReport" />
  </div>
  <q-dialog v-model="displayReportNameDialog" transition-duration="100">
    <q-card style="width: 400px">
      <q-card-section>
        <div class="text-h6">Name your report</div>
      </q-card-section>
      <q-card-section class="q-pt-none">
        <q-input
          v-model="reportCopy.name"
          placeholder="Enter a name"
          dense
          autofocus
          @keyup.enter="submit"
        />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn v-close-popup no-caps unelevated flat label="Cancel" />
        <q-btn no-caps unelevated color="primary" @click="submit">Save</q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
  <q-dialog v-model="deleteDialog" transition-duration="100">
    <q-card style="width: 400px">
      <q-card-section class="row items-center">
        <span class="q-ml-sm">
          Are you sure you want to permanently delete <b>{{ reportCopy.name }}</b
          >?
        </span>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
        <q-btn
          v-close-popup
          no-caps
          unelevated
          label="Delete"
          color="red"
          :loading="deleteLoading"
          @click="$emit('delete')"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { toRaw, type PropType } from 'vue'
import type { MarketReportSubscriptionAttributes } from 'common'
import EnterMarkets from './EnterMarkets.vue'

export default {
  name: 'MarketReportSettings',
  components: {
    EnterMarkets
  },
  props: {
    report: {
      type: Object as PropType<MarketReportSubscriptionAttributes>,
      required: true
    }
  },
  emits: ['submit', 'delete'],
  data() {
    return {
      reportCopy: this.report,
      deleteDialog: false,
      deleteLoading: false,
      displayReportNameDialog: false,
      isSubmitting: false
    }
  },
  computed: {
    isEditMode() {
      return this.report.id !== -1
    },
    canSubmit() {
      return this.reportCopy.filters.coordinates.length
    }
  },
  created() {
    // impossible to copy a reactive object, so we need to clone it
    this.reportCopy = structuredClone(toRaw(this.report))
  },
  methods: {
    updateReport(report: MarketReportSubscriptionAttributes) {
      this.reportCopy = report
    },
    submit() {
      if (!this.reportCopy.name && !this.report.name) {
        this.reportCopy.name = `Regional Report (${new Date().toISOString().split('T')[0]})`
        // we display the dialog to allow the user to change the report name
        this.displayReportNameDialog = true
        return
      }

      this.displayReportNameDialog = false
      this.isSubmitting = true
      this.$emit('submit', this.reportCopy)
    }
  }
}
</script>
<style scoped>
.market-report {
  max-width: 1600px;
}
</style>
